import React, { useContext, useEffect, useMemo, useState } from 'react';
import { history } from '../../Routes/history';
import { ROUTE_NAMES } from '../../Routes/Routes';
import {
  Container,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Typography
} from '@material-ui/core';
import {
  getConstructorContractAPI,
  getConstructorContractsAPI,
  getConstructorInvoicesAPI
} from '../../API/Constructors/ConstructorsAPI';
import {
  extractErrorMessage,
  redirectOnAuthFailure
} from '../../Utils/Errors/Errors';
import { AlertsDispatchContext } from '../../Contexts/AlertsContext';
import { UserContext, UserDispatchContext } from '../../Contexts/UserContext';
import { get as _get } from 'lodash';
import { ALERT_TYPE } from '../../Components/Alerts/alert_enums';
import { logoutUser } from '../../Utils/User/Actions';
import BaseBreadcrumbs from '../../Components/Breadcrumbs/BaseBreadcrumbs';
import { Company } from '../../Configs/general';
import { ConstructorContractsContext } from '../../Contexts/ContractsContext';
import ConstructorOptions from './ConstructorOptions';
import moment from 'moment-timezone';
import ConstructorInvoiceCard from '../../Components/Cards/ConstructorInvoiceCard/ConstructorInvoiceCard';
import ErrorState from '../../Components/EmptyState/ErrorState';
import ConstructorPhaseHeader from './ConstructorPhaseHeader';
import ContactMeDialog from '../../Components/Dialogs/ContactMeDialog';
import { PARSED_CONSTRUCTOR_CLIENT_IDENTIFICATION_TYPE } from '../../Enums/constructors';

const parseProjectsData = (projects, contracts) => {
  const contractIdsSet = new Set(contracts.map(contract => contract.id));

  return projects.map(project => {
    return {
      label: project.name,
      value: project.id,
      phases: project.phases
        .filter(phase => contractIdsSet.has(phase.contractId))
        .map(phase => ({
          label: `${phase.name} #${phase.contractId}`,
          value: phase.contractId
        }))
    };
  });
};

const findOwnProject = (projects, contractId) => {
  return projects.find(project => {
    const phaseContractIdSet = new Set(
      project.phases.map(phase => phase.contractId)
    );
    return phaseContractIdSet.has(contractId);
  });
};

const ConstructorPhase = props => {
  const {
    match: {
      params: { contractId: paramsContractId }
    }
  } = props;

  const classes = useStyles();
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const setAlert = useContext(AlertsDispatchContext);
  const constructorContracts = useContext(ConstructorContractsContext);

  const [projects, setProjects] = useState([]);
  const [constructorInfo, setConstructorInfo] = useState(null);
  const [phase, setPhase] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('');

  const [openContactMeDialog, setOpenContactMeDialog] = useState(false);

  const phases = useMemo(() => {
    if (!selectedProject || projects.length === 0) {
      return [];
    }

    const project = projects.find(p => p.value === selectedProject);

    return project ? project.phases : [];
  }, [selectedProject, projects]);

  const contractId = Number(paramsContractId);

  const authToken = _get(currentUser, 'token');

  // Parse contract's id
  useEffect(() => {
    if (Number.isNaN(contractId)) {
      history.push(ROUTE_NAMES.constructors);
      return;
    }

    if (!authToken) {
      history.push('/');
      return;
    }

    if (!constructorContracts || !constructorContracts.length) {
      history.push('/');
      return;
    }

    if (!constructorContracts.find(contract => contract.id === contractId)) {
      history.push(ROUTE_NAMES.constructors);
      return;
    }

    let ignoreRequest = false;

    const fetchData = async () => {
      setIsLoading(true);
      const response = await getConstructorContractAPI(authToken, contractId);

      if (ignoreRequest) {
        return;
      }

      if (!response.success) {
        setIsLoading(false);
        if (
          redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
        ) {
          return;
        }

        setAlert({
          type: ALERT_TYPE.ERROR,
          message: extractErrorMessage(response).message
        });
        return;
      }

      const phaseData = response.data.phase;
      setPhase(phaseData);
      setSelectedProject(phaseData.project.id);
      setSelectedPhase(phaseData.contractId);
      setIsLoading(false);
    };

    fetchData();

    return () => {
      ignoreRequest = true;
    };
  }, [contractId, authToken, constructorContracts, setAlert, setCurrentUser]);

  // Fetch projects data
  useEffect(() => {
    let ignoreRequest = false;
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      const response = await getConstructorContractsAPI(authToken);

      if (ignoreRequest) {
        return;
      }

      if (!response.success) {
        setIsLoadingProjects(false);
        if (
          redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
        ) {
          return;
        }

        setAlert({
          type: ALERT_TYPE.ERROR,
          message: 'Ocurrió un error, intente más tarde'
        });
        return;
      }

      const projectsData = parseProjectsData(
        response.data.projects,
        constructorContracts
      );

      const project = findOwnProject(response.data.projects, contractId);

      setIsLoadingProjects(false);
      setProjects(projectsData);

      //This information is used for requests creations
      setConstructorInfo({
        projectName: project.name,
        projectConsecutive: project.consecutive,
        businessName: project.client.name,
        businessIdentification: project.client.identification,
        businessIdentificationType:
          PARSED_CONSTRUCTOR_CLIENT_IDENTIFICATION_TYPE[
            project.client.identificationType
          ]
      });
    };

    fetchProjects();
    return () => {
      ignoreRequest = true;
    };
  }, [constructorContracts, contractId, authToken, setAlert, setCurrentUser]);

  // Fetch invoices data
  useEffect(() => {
    if (!phase) {
      return;
    }

    let ignoreRequest = false;

    const fetchData = async () => {
      setIsLoadingInvoice(true);
      const response = await getConstructorInvoicesAPI(authToken, contractId);

      if (ignoreRequest) {
        return;
      }

      if (!response.success) {
        setInvoice(null);
        setIsLoadingInvoice(false);

        if (
          redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
        ) {
          return;
        }
        return;
      }

      const invoicesData = response.data.invoices;
      setIsLoadingInvoice(false);

      if (!invoicesData || invoicesData.length === 0) {
        setInvoice(null);
        return;
      }

      setInvoice(invoicesData[0]);
    };
    fetchData();
    return () => {
      ignoreRequest = true;
    };
  }, [phase, contractId, authToken, setCurrentUser, setAlert]);
  const onOpenContactMeDialog = () => {
    setOpenContactMeDialog(true);
  };

  const onCloseContactMeDialog = () => {
    setOpenContactMeDialog(false);
  };

  if (!phase && !isLoading) {
    return (
      <ErrorState messageTitle="No se ha encontrado información de esta etapa" />
    );
  }

  return (
    <>
      {openContactMeDialog && contractId && (
        <ContactMeDialog
          onClose={onCloseContactMeDialog}
          contractId={contractId}
        />
      )}
      <Container maxWidth="md" className={classes.root}>
        <BaseBreadcrumbs
          classes={classes}
          separator=">"
          breadcrumbs={[
            {
              link: ROUTE_NAMES.constructors,
              name: 'Proyectos'
            },
            {
              link: `${ROUTE_NAMES.constructorPhase}/${contractId}`,
              name: `Detalle ${Company.contractConjugation.regular.singular.contraction}`
            }
          ]}
        />
        <Grid container>
          <Grid item xs={12}>
            <ConstructorPhaseHeader
              classes={classes}
              phases={phases}
              projects={projects}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              selectedPhase={selectedPhase}
              setSelectedPhase={setSelectedPhase}
              isLoading={isLoading || isLoadingProjects}
              phase={phase}
            />
          </Grid>
          <Grid item xs={12}>
            <ConstructorInvoiceCard
              invoiceDetails={invoice}
              loading={isLoadingInvoice}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" className={classes.container}>
              <Typography className={classes.text}>
                <strong>Otros servicios:</strong>
              </Typography>

              <ConstructorOptions
                contractId={contractId}
                onOpenContactMeDialog={onOpenContactMeDialog}
                constructorInfo={constructorInfo}
              />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            {phase && phase.comments && phase.comments.length > 0 && (
              <Paper variant="outlined" className={classes.container}>
                <Grid container spacing={2} alignContent="space-between">
                  <Grid item xs={12}>
                    <Typography className={classes.text}>
                      <strong>Observaciones</strong>
                    </Typography>
                    <Divider />
                  </Grid>
                  {phase.comments.map(({ id, author, comment, createdAt }) => (
                    <Grid item xs={12} key={id}>
                      <Typography className={classes.text}>
                        <em>
                          {author} (
                          {moment(createdAt)
                            .tz('America/Bogota')
                            .format('DD/MM/YYYY')}
                          ):
                        </em>
                      </Typography>
                      <Typography className={classes.text}>
                        {comment}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(4)
  },
  breadcrumbRoot: {
    marginBottom: theme.spacing(2)
  },
  container: {
    padding: theme.spacing(2)
  },
  title: {
    fontSize: '1.25em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1em'
    }
  },
  subtitle: {
    fontSize: '1em',
    color: theme.palette.text.greyDark,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8em'
    }
  },
  text: {
    fontSize: '1em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8em'
    }
  },
  phasesDivider: {
    minWidth: '100%',
    marginBottom: theme.spacing(2)
  }
}));

export default ConstructorPhase;
